import React, { useState } from "react";
import {
  Container,
  Grid,
  Typography,
  TextField,
  Slider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Settings } from "@mui/icons-material";

const EVBuildingDashboard = () => {
  const [buildings, setBuildings] = useState([
    {
      id: 1,
      name: "3400 Central Expy",
      targetConsumption: 50, // kWh
      maxLoad: 80, // kW
      maxBatteryLevel: 90, // %
      maxSingleEVPowerOutput: 50, // kW
      offPeakCharging: true,
      minBatteryLevel: 20, // %      
      managerEmail: "ash.durna@nissan-usa.com",
    },
  ]);

  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [isConfigOpen, setIsConfigOpen] = useState(false);

  // Handle configuration changes
  const handleConfigChange = (key, value) => {
    setSelectedBuilding({ ...selectedBuilding, [key]: value });

    // Update the building in the list
    setBuildings(
      buildings.map((b) =>
        b.id === selectedBuilding.id ? { ...b, [key]: value } : b
      )
    );
  };

  // Reset Configuration to Default
  const resetSettings = () => {
    const defaultConfig = {
      targetConsumption: 50,
      maxLoad: 80,
      maxBatteryLevel: 90,
      maxSingleEVPowerOutput: 50,
      offPeakCharging: true,
      minBatteryLevel: 20,      
      managerEmail: "",
    };

    setSelectedBuilding({ ...selectedBuilding, ...defaultConfig });
    setBuildings(
      buildings.map((b) =>
        b.id === selectedBuilding.id ? { ...b, ...defaultConfig } : b
      )
    );
  };

  // Add a New Building
  const addBuilding = () => {
    const newBuilding = {
      id: buildings.length + 1,
      name: `Building ${String.fromCharCode(65 + buildings.length)}`,
      targetConsumption: 50,
      maxLoad: 80,
      maxBatteryLevel: 90,
      maxSingleEVPowerOutput: 50,
      offPeakCharging: true,
      minBatteryLevel: 20,
      managerEmail: "",
    };
    setBuildings([...buildings, newBuilding]);
  };

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        EV Chargers Management - Buildings Configuration
      </Typography>

      {/* Buildings Table */}
      <TableContainer component={Paper} sx={{ marginBottom: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Building Name</TableCell>
              <TableCell align="right">Target Consumption (kWh)</TableCell>
              <TableCell align="right">Max Load (kW)</TableCell>
              <TableCell align="right">Max Battery Level (%)</TableCell>
              <TableCell align="right">Max EV Power Output (kW)</TableCell>
              <TableCell align="right">Min Battery Level (%)</TableCell>              
              <TableCell align="right">Manager Email</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {buildings.map((building) => (
              <TableRow key={building.id}>
                <TableCell>{building.name}</TableCell>
                <TableCell align="right">{building.targetConsumption}</TableCell>
                <TableCell align="right">{building.maxLoad}</TableCell>
                <TableCell align="right">{building.maxBatteryLevel}</TableCell>
                <TableCell align="right">{building.maxSingleEVPowerOutput}</TableCell>
                <TableCell align="right">{building.minBatteryLevel}</TableCell>                
                <TableCell align="right">{building.managerEmail}</TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => {
                      setSelectedBuilding(building);
                      setIsConfigOpen(true);
                    }}
                  >
                    <Settings color="primary" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Building Button */}
      <Button variant="contained" color="success" onClick={addBuilding}>
        Add Building
      </Button>

      {/* Configuration Dialog */}
      {selectedBuilding && (
        <Dialog open={isConfigOpen} onClose={() => setIsConfigOpen(false)} fullWidth>
          <DialogTitle>Configure {selectedBuilding.name}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              {/* Target Consumption */}
              <Grid item xs={12} sm={6}>
                <Typography>Target Consumption (kWh)</Typography>
                <Slider
                  value={selectedBuilding.targetConsumption}
                  onChange={(e, val) => handleConfigChange("targetConsumption", val)}
                  min={10}
                  max={100}
                  step={5}
                  valueLabelDisplay="auto"
                />
              </Grid>

              {/* Maximum Load */}
              <Grid item xs={12} sm={6}>
                <Typography>Max Load (kW)</Typography>
                <Slider
                  value={selectedBuilding.maxLoad}
                  onChange={(e, val) => handleConfigChange("maxLoad", val)}
                  min={10}
                  max={200}
                  step={10}
                  valueLabelDisplay="auto"
                />
              </Grid>

              {/* Max Battery Level */}
              <Grid item xs={12} sm={6}>
                <Typography>Max Battery Level (%)</Typography>
                <Slider
                  value={selectedBuilding.maxBatteryLevel}
                  onChange={(e, val) => handleConfigChange("maxBatteryLevel", val)}
                  min={50}
                  max={100}
                  step={5}
                  valueLabelDisplay="auto"
                />
              </Grid>

              {/* Max Single EV Power Output */}
              <Grid item xs={12} sm={6}>
                <Typography>Max Single EV Power Output (kW)</Typography>
                <Slider
                  value={selectedBuilding.maxSingleEVPowerOutput}
                  onChange={(e, val) => handleConfigChange("maxSingleEVPowerOutput", val)}
                  min={10}
                  max={100}
                  step={5}
                  valueLabelDisplay="auto"
                />
              </Grid>

              {/* Manager Email */}
              <Grid item xs={12}>
                <Typography>Building Manager Email</Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={selectedBuilding.managerEmail}
                  onChange={(e) => handleConfigChange("managerEmail", e.target.value)}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsConfigOpen(false)} color="secondary">
              Close
            </Button>
            <Button onClick={resetSettings} color="primary">
              Reset to Defaults
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default EVBuildingDashboard;
